/**
 * SRES · Composite Builder — Sprint P37z UI (Feb 2026)
 * ----------------------------------------------------
 * Wraps the new backend endpoints:
 *   POST /api/projects/{pid}/composites/build
 *   GET  /api/projects/{pid}/composites/latest
 *   GET  /api/projects/{pid}/composites/history
 *
 * Mounted BELOW the legacy CompositeStep preview so both live side by
 * side — the top preview reads the legacy `compositePreviewFull` route
 * (kept for backward-compat), while this panel writes durable runs to
 * `db.composites` that downstream stages consume.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Stack, PlayCircle, CheckCircle, ClockClockwise, Sparkle,
} from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { WizardCard, KV } from "@/pages/wizard/_shared";

// P2 cross-panel wiring — TopCutReviewPanel writes the recommended
// top-cut for this project into this localStorage key.
const LS_KEY = (pid) => `sres.composite.suggested_top_cut.${pid}`;

export default function CompositeBuilderPanel({ projectId }) {
  const qc = useQueryClient();
  const [length, setLength]         = useState(1.0);
  const [topCut, setTopCut]         = useState("");
  const [minRec, setMinRec]         = useState("");
  const [honourDomains,   setHD]    = useState(true);
  const [honourLithology, setHL]    = useState(true);
  const [tcSource,        setTcSrc] = useState(null);   // "top-cut-review" | null

  // On mount, if the Top-cut Review panel wrote a suggested value into
  // localStorage for this project, adopt it as the current top-cut input.
  // Guard with Number.isFinite so a legacy / malformed LS blob doesn't
  // populate the field with `"NaN"` or an object (iter76 · defensive).
  useEffect(() => {
    if (!projectId) return;
    try {
      const raw = localStorage.getItem(LS_KEY(projectId));
      const num = Number(raw);
      if (raw && Number.isFinite(num) && num > 0 && !topCut) {
        setTopCut(String(num));
        setTcSrc("top-cut-review");
      }
      // Consume the key regardless — if it was garbage, we clean it up.
      if (raw) localStorage.removeItem(LS_KEY(projectId));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const latestQ = useQuery({
    queryKey: ["composites-latest", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/composites/latest`)
                          .then((r) => r.data).catch(() => null),
    enabled:  !!projectId,
  });
  const historyQ = useQuery({
    queryKey: ["composites-history", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/composites/history?limit=10`)
                          .then((r) => r.data),
    enabled:  !!projectId,
  });

  const buildMut = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/composites/build`, {
      composite_length_m: length,
      honour_domains:      honourDomains,
      honour_lithology:    honourLithology,
      top_cut:      topCut ? Number(topCut) : null,
      min_recovery: minRec ? Number(minRec) : null,
    }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Built ${data.summary?.n_composites} composites at ${length} m.`);
      qc.invalidateQueries({ queryKey: ["composites-latest",  projectId] });
      qc.invalidateQueries({ queryKey: ["composites-history", projectId] });
    },
    onError: (e) => toast.error(
      e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message
    ),
  });

  const latest = latestQ.data;
  const summary = latest?.summary || {};

  return (
    <WizardCard title="Composite Builder — persistent run"
                    testId="cb-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left: parameters */}
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">
              Composite length (m) — {length.toFixed(2)}
            </Label>
            <Slider
              min={0.5} max={10} step={0.5}
              value={[length]}
              onValueChange={([v]) => setLength(v)}
              data-testid="cb-length"
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <Label className="text-[11px] font-semibold text-slate-600">
                Top-cut (optional)
                {tcSource === "top-cut-review" && (
                  <span
                    className="ml-1.5 text-[10px] uppercase tracking-widest px-1 py-0.5 rounded bg-brand-blue/10 text-brand-blue border border-brand-blue/30 inline-flex items-center gap-0.5"
                    title="Populated from the Top-cut Review panel"
                    data-testid="cb-topcut-from-review"
                  >
                    <Sparkle size={9} weight="fill" /> from review
                  </span>
                )}
              </Label>
              <Input value={topCut}
                        onChange={(e) => { setTopCut(e.target.value); setTcSrc(null); }}
                        placeholder="e.g. 30.0"
                        data-testid="cb-topcut" />
            </label>
            <label className="block">
              <Label className="text-[11px] font-semibold text-slate-600">
                Min recovery (0-1)
              </Label>
              <Input value={minRec} onChange={(e) => setMinRec(e.target.value)}
                        placeholder="e.g. 0.85"
                        data-testid="cb-minrec" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input type="checkbox" checked={honourDomains}
                       onChange={(e) => setHD(e.target.checked)}
                       data-testid="cb-honour-domains" />
            Honour mineralised domains
          </label>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input type="checkbox" checked={honourLithology}
                       onChange={(e) => setHL(e.target.checked)}
                       data-testid="cb-honour-lithology" />
            Honour lithology boundaries
          </label>
          <Button
            onClick={() => buildMut.mutate()}
            disabled={buildMut.isPending}
            data-testid="cb-build"
            className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white gap-1.5">
            <PlayCircle size={13} weight="fill" />
            {buildMut.isPending ? "Building…" : "Build & Save composites"}
          </Button>
        </div>

        {/* Right: latest run summary */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500">
            <Stack size={12} /> Latest run
          </div>
          {!latest && (
            <div className="text-[12px] text-slate-500 italic border border-dashed border-slate-300 rounded p-3">
              No composite runs yet — set parameters and click "Build".
            </div>
          )}
          {latest && (
            <div className="border border-slate-200 rounded p-3 bg-slate-50 space-y-1"
                    data-testid="cb-latest">
              <KV k="Run ID"           v={latest.id?.slice(0, 20) || "—"} />
              <KV k="Composite length" v={`${latest.parameters?.composite_length_m ?? "—"} m`} />
              <KV k="N composites"     v={summary.n_composites?.toLocaleString?.() ?? "—"} />
              <KV k="N holes"          v={summary.n_holes ?? "—"} />
              <KV k="Mean grade"       v={fmt(summary.mean_grade, 4)} />
              <KV k="Median grade"     v={fmt(summary.median_grade, 4)} />
              <KV k="Max grade"        v={fmt(summary.max_grade, 4)} />
              <KV k="Std grade"        v={fmt(summary.std_grade, 4)} />
              {latest.parameters?.top_cut && (
                <KV k="Top-cut applied"  v={fmt(latest.parameters.top_cut, 3)} />
              )}
              <div className="text-[10.5px] text-slate-400 mt-1.5">
                Built {new Date(latest.created_at).toLocaleString()} by {latest.created_by || "—"}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {historyQ.data?.items?.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">
            <ClockClockwise size={12} /> Recent runs
          </div>
          <div className="border border-slate-200 rounded overflow-hidden text-[11.5px]"
                  data-testid="cb-history">
            <table className="w-full">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Run ID</th>
                  <th className="text-right px-2 py-1.5">Length</th>
                  <th className="text-right px-2 py-1.5">N comp</th>
                  <th className="text-right px-2 py-1.5">Mean</th>
                  <th className="text-left px-2 py-1.5">Built</th>
                </tr>
              </thead>
              <tbody>
                {historyQ.data.items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono text-[10.5px]">{r.id}</td>
                    <td className="px-2 py-1 text-right">{r.parameters?.composite_length_m ?? "—"} m</td>
                    <td className="px-2 py-1 text-right">{r.summary?.n_composites ?? "—"}</td>
                    <td className="px-2 py-1 text-right">{fmt(r.summary?.mean_grade, 4)}</td>
                    <td className="px-2 py-1 text-[10.5px] text-slate-500">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </WizardCard>
  );
}

function fmt(v, d = 2) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
}
